import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../components/AuthContext";
import { usePermissions, PermissionsProvider } from "../hooks/usePermissions";
import { PermissionGate } from "../components/ProtectedRoute";
import OnboardingWizard from "../components/OnboardingWizard";
import DevPanel from "../components/DevPanel";

const linkStyle = ({ isActive }) => ({
  display: "block",
  padding: "12px 16px",
  borderRadius: 8,
  textDecoration: "none",
  fontWeight: 600,
  fontSize: 14,
  color: isActive ? "#DC2626" : "#374151",
  background: isActive ? "#FEF2F2" : "transparent",
});

function SectionTitle({ children }) {
  return <div style={{ padding: "16px 16px 8px", fontSize: 11, fontWeight: 700, color: "#9CA3AF", letterSpacing: 1 }}>{children}</div>;
}

function AppLayoutInner() {
  const { role, adminEmail, carrierEmail, shipperEmail, logout } = useAuth();
  const { role: permRole } = usePermissions();
  const location = useLocation();
  const [organization, setOrganization] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  const displayRole = (role === "BROKER" ? (permRole || "BROKER_ADMIN") : role) || permRole;
  const userEmail = adminEmail || carrierEmail || shipperEmail;

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) setSidebarOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close sidebar on navigation (mobile)
  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
  }, [location.pathname, isMobile]);

  useEffect(() => {
    async function loadOrg() {
      try {
        const headers = {};
        if (adminEmail) headers['x-admin-email'] = adminEmail;
        if (shipperEmail) headers['x-shipper-email'] = shipperEmail;
        if (carrierEmail) headers['x-carrier-email'] = carrierEmail;
        
        const res = await fetch('/api/organization/my-org', { headers });
        if (res.ok) {
          const data = await res.json();
          setOrganization(data.organization);
        }
      } catch (e) {
        console.error('Failed to load org:', e);
      }
    }
    if (userEmail) loadOrg();
  }, [adminEmail, shipperEmail, carrierEmail, userEmail]);

  const sidebarContent = (
    <>
      <div style={{ padding: 20, borderBottom: "1px solid #E5E7EB" }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#DC2626" }}>RoadOptix</div>
          {isMobile && (
            <button onClick={() => setSidebarOpen(false)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#6b7280' }}>×</button>
          )}
        </div>
        {organization && (
          <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginTop: 4 }}>{organization.name}</div>
        )}
        <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>{userEmail}</div>
      </div>

      <nav style={{ flex: 1, padding: "8px 12px", overflowY: "auto" }}>
        {/* BROKER PORTAL */}
        {(displayRole === "BROKER_ADMIN" || displayRole === "BROKER_AGENT") && (
          <>
            <SectionTitle>BROKER PORTAL</SectionTitle>
            <NavLink to="/app/broker" end style={linkStyle}>📊 Dashboard</NavLink>
            <PermissionGate permission="loads.view">
              <NavLink to="/app/broker/loads" style={linkStyle}>📦 Loads</NavLink>
            </PermissionGate>
            <PermissionGate permission="loads.create">
              <NavLink to="/app/broker/create" style={linkStyle}>➕ Create Load</NavLink>
            </PermissionGate>
            <PermissionGate permission="customers.view">
              <NavLink to="/app/broker/customers" style={linkStyle}>🏭 Shippers</NavLink>
            </PermissionGate>
            <PermissionGate permission="carriers.view">
              <NavLink to="/app/broker/carriers" style={linkStyle}>🚛 Carriers</NavLink>
            </PermissionGate>
            <NavLink to="/app/broker/copilot" style={linkStyle}>🤖 AI Copilot</NavLink>
              <NavLink to="/app/broker/alerts" style={linkStyle}>🚨 Alerts</NavLink>
              <NavLink to="/app/broker/freight-agent" style={linkStyle}>🤖 Freight Agent</NavLink>
              <NavLink to="/app/broker/document-signing" style={linkStyle}>✍️ Document Signing</NavLink>
              <NavLink to="/app/broker/blockchain" style={linkStyle}>⛓️ Blockchain</NavLink>
              <NavLink to="/app/broker/analytics" style={linkStyle}>🧠 AI Analytics</NavLink>
            <PermissionGate permission="crm.view">
              <NavLink to="/app/broker/crm" style={linkStyle}>👥 CRM</NavLink>
            </PermissionGate>
            <PermissionGate permission="tracking.view">
              <NavLink to="/app/broker/tracking" style={linkStyle}>📍 GPS Tracking</NavLink>
              <NavLink to="/app/broker/sustainability" style={linkStyle}>🌱 Sustainability</NavLink>
            </PermissionGate>
          </>
        )}

        {/* ACCOUNTING PORTAL */}
        {displayRole === "ACCOUNTING" && (
          <>
            <SectionTitle>ACCOUNTING</SectionTitle>
            <NavLink to="/app/accounting" end style={linkStyle}>📊 Dashboard</NavLink>
            <NavLink to="/app/accounting/invoicing" style={linkStyle}>📄 Invoicing</NavLink>
            <NavLink to="/app/accounting/settlements" style={linkStyle}>💰 Settlements</NavLink>
            <NavLink to="/app/accounting/exports" style={linkStyle}>📁 Exports</NavLink>
            <NavLink to="/app/accounting/integrations" style={linkStyle}>🔗 Integrations</NavLink>
            <SectionTitle>READ-ONLY</SectionTitle>
            <NavLink to="/app/broker/loads" style={linkStyle}>📦 View Loads</NavLink>
            <NavLink to="/app/broker/carriers" style={linkStyle}>🚛 View Carriers</NavLink>
          </>
        )}

        {/* SHIPPER PORTAL */}
        {displayRole === "SHIPPER" && (
          <>
            <SectionTitle>SHIPPER PORTAL</SectionTitle>
            <NavLink to="/app/shipper" end style={linkStyle}>📊 Dashboard</NavLink>
            <NavLink to="/app/shipper/tracking" style={linkStyle}>📍 Track Shipments</NavLink>
            <NavLink to="/app/shipper/loads" style={linkStyle}>📦 All Loads</NavLink>
            <NavLink to="/app/shipper/create" style={linkStyle}>➕ Request Shipment</NavLink>
            <NavLink to="/app/shipper/profile" style={linkStyle}>⚙️ My Preferences</NavLink>
          </>
        )}

        {/* CARRIER PORTAL */}
        {displayRole === "CARRIER" && (
          <>
            <SectionTitle>CARRIER PORTAL</SectionTitle>
            <NavLink to="/app/carrier/assigned" style={linkStyle}>📦 Assigned Loads</NavLink>
            <NavLink to="/app/carrier/board" style={linkStyle}>📋 Load Board</NavLink>
            <NavLink to="/app/carrier/factoring" style={linkStyle}>💵 Quick Pay</NavLink>
            <NavLink to="/app/carrier/certifications" style={linkStyle}>🎓 Certifications</NavLink>
            <NavLink to="/app/carrier/profile" style={linkStyle}>👤 Profile</NavLink>
          </>
        )}

        {/* ADMIN SECTION */}
        {displayRole === "BROKER_ADMIN" && (
          <>
            <SectionTitle>ADMIN</SectionTitle>
            <NavLink to="/app/broker/admin/users" style={linkStyle}>👤 Users</NavLink>
            <NavLink to="/app/broker/admin/roles" style={linkStyle}>🔐 Roles</NavLink>
            <NavLink to="/app/broker/admin/organization" style={linkStyle}>🏢 Organization</NavLink>
            <NavLink to="/app/broker/admin/organizations" style={linkStyle}>🏢 Organizations</NavLink>
            <NavLink to="/app/broker/admin/audit" style={linkStyle}>📋 Audit Log</NavLink>
            <NavLink to="/app/broker/admin/security" style={linkStyle}>🔒 Security</NavLink>
            <NavLink to="/app/broker/sap-settings" style={linkStyle}>⚙️ SAP</NavLink>
          </>
        )}
      </nav>

      <div style={{ padding: 16, borderTop: "1px solid #E5E7EB" }}>
        {organization && (
          <div style={{ marginBottom: 12, padding: 10, background: "#F3F4F6", borderRadius: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ 
                fontSize: 12, 
                fontWeight: 700, 
                color: organization.tier === 'ENTERPRISE' ? '#7c3aed' : organization.tier === 'PROFESSIONAL' ? '#2563eb' : '#059669'
              }}>
                {organization.tier}
              </span>
              <span style={{ 
                fontSize: 10, 
                padding: "2px 6px", 
                borderRadius: 4, 
                background: organization.subscriptionStatus === 'ACTIVE' ? '#d1fae5' : '#fef3c7',
                color: organization.subscriptionStatus === 'ACTIVE' ? '#065f46' : '#92400e'
              }}>
                {organization.subscriptionStatus}
              </span>
            </div>
          </div>
        )}
        <button onClick={logout} style={{ width: "100%", padding: "12px", background: "#DC2626", color: "white", border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}>
          Logout
        </button>
      </div>
    </>
  );

  return (
    <div style={{ display: "flex", minHeight: "100vh" }}>
      {/* Mobile Overlay */}
      {isMobile && sidebarOpen && (
        <div 
          onClick={() => setSidebarOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 998 }}
        />
      )}

      {/* Sidebar */}
      <aside style={{ 
        width: 260, 
        background: "#F9FAFB", 
        borderRight: "1px solid #E5E7EB", 
        display: "flex", 
        flexDirection: "column",
        position: isMobile ? 'fixed' : 'relative',
        left: isMobile ? (sidebarOpen ? 0 : -260) : 0,
        top: 0,
        bottom: 0,
        zIndex: 999,
        transition: 'left 0.3s ease'
      }}>
        {sidebarContent}
      </aside>

      {/* Main Content */}
      <main style={{ flex: 1, background: "#F3F4F6", minHeight: "100vh", minWidth: 0 }}>
        {/* Mobile Header */}
        <div style={{ 
          padding: "12px 16px", 
          background: "white", 
          borderBottom: "1px solid #E5E7EB", 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center",
          position: 'sticky',
          top: 0,
          zIndex: 100
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {isMobile && (
              <button 
                onClick={() => setSidebarOpen(true)}
                style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', padding: 0 }}
              >
                ☰
              </button>
            )}
            {organization && (
              <>
                <span style={{ fontSize: isMobile ? 14 : 16, fontWeight: 700, color: "#111827" }}>
                  {isMobile ? 'RoadOptix' : organization.name}
                </span>
                {!isMobile && (
                  <span style={{ 
                    fontSize: 11, 
                    padding: "3px 8px", 
                    borderRadius: 12, 
                    background: organization.tier === 'ENTERPRISE' ? '#ede9fe' : '#dbeafe',
                    color: organization.tier === 'ENTERPRISE' ? '#7c3aed' : '#2563eb',
                    fontWeight: 600
                  }}>
                    {organization.tier}
                  </span>
                )}
              </>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {!isMobile && <span style={{ fontSize: 13, color: "#6B7280" }}>{userEmail}</span>}
            <button onClick={logout} style={{ padding: "6px 12px", background: "#FEF2F2", color: "#DC2626", border: "none", borderRadius: 6, fontWeight: 600, cursor: "pointer" }}>
              {isMobile ? '🚪' : 'Logout'}
            </button>
          </div>
        </div>
        
        <div style={{ padding: isMobile ? 12 : 24 }}>
          <Outlet />
        </div>
        
        <footer style={{ padding: "16px 24px", textAlign: "center", color: "#9CA3AF", fontSize: 12 }}>
          Powered by <strong>RoadOptix</strong> © 2026
        </footer>
      </main>

      <DevPanel />
      {/* Onboarding Wizard - Only for admins, hide on mobile */}
      {displayRole === "BROKER_ADMIN" && showOnboarding && !isMobile && (
        <OnboardingWizard onDismiss={() => setShowOnboarding(false)} />
      )}
    </div>
  );
}

export default function AppLayout() {
  return (
    <PermissionsProvider>
      <AppLayoutInner />
    </PermissionsProvider>
  );
}
